import { useState, useEffect, useCallback } from 'react'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import {
    exportMultiSheet,
    buildCCDailySheet,
    buildCCMonthlySheet,
    buildCCQuarterlySheets
} from '../utils/exportExcel'

const FORMATS = [
    { id: 'daily',     label: 'Daily',     description: 'One row per lease per day — ComboCurve daily upload' },
    { id: 'monthly',   label: 'Monthly',   description: 'One row per lease per month — ComboCurve monthly upload' },
    { id: 'quarterly', label: 'Quarterly', description: 'ComboCurve upload tab + investor summary tab' }
]

function todayStr() {
    return new Date().toISOString().slice(0, 10)
}

function daysAgoStr(n) {
    const d = new Date()
    d.setDate(d.getDate() - n)
    return d.toISOString().slice(0, 10)
}

function startOfYearStr() {
    return `${new Date().getFullYear()}-01-01`
}

function defaultDates(format) {
    if (format === 'daily')     return { start: daysAgoStr(90), end: daysAgoStr(2) }
    if (format === 'quarterly') return { start: startOfYearStr(), end: daysAgoStr(2) }
    return { start: startOfYearStr(), end: daysAgoStr(2) }  // monthly
}

export default function DownloadsDashboard() {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    // ---- State ----
    const [format, setFormat]               = useState('monthly')
    const [startDate, setStartDate]         = useState(startOfYearStr())
    const [endDate, setEndDate]             = useState(daysAgoStr(2))
    const [leaseList, setLeaseList]         = useState([])       // all 90 leases from /api/downloads
    const [selectedLeases, setSelected]     = useState(new Set()) // INPT_IDs
    const [filter, setFilter]               = useState('')        // text search
    const [data, setData]                   = useState(null)      // fetched rows
    const [loadingLeases, setLoadingLeases] = useState(true)
    const [loadingData, setLoadingData]     = useState(false)
    const [error, setError]                 = useState(null)

    // ---- Load lease list on mount ----
    useEffect(() => {
        async function fetchLeases() {
            setLoadingLeases(true)
            setError(null)
            try {
                const res = await fetch(`${apiConfig.baseUrl}/downloads`)
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const json = await res.json()
                setLeaseList(json.leases || [])
                // Default: select all leases that have data
                const withData = new Set((json.leases || []).filter(l => l.has_data).map(l => l.inpt_id))
                setSelected(withData)
            } catch (e) {
                setError(`Failed to load lease list: ${e.message}`)
            } finally {
                setLoadingLeases(false)
            }
        }
        fetchLeases()
    }, [])

    // ---- Fetch data when format/dates change ----
    const fetchData = useCallback(async () => {
        if (!startDate || !endDate) return
        setLoadingData(true)
        setError(null)
        setData(null)
        try {
            const params = new URLSearchParams({ format, start: startDate, end: endDate })
            const res = await fetch(`${apiConfig.baseUrl}/downloads/data?${params}`)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const json = await res.json()
            setData(json)
        } catch (e) {
            setError(`Failed to fetch production data: ${e.message}`)
        } finally {
            setLoadingData(false)
        }
    }, [format, startDate, endDate])

    useEffect(() => { fetchData() }, [fetchData])

    // ---- Format change → reset dates ----
    function handleFormatChange(f) {
        const dates = defaultDates(f)
        setFormat(f)
        setStartDate(dates.start)
        setEndDate(dates.end)
    }

    // ---- Lease selection helpers ----
    function toggleLease(inpt_id) {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(inpt_id)) next.delete(inpt_id)
            else next.add(inpt_id)
            return next
        })
    }

    function selectAll() {
        setSelected(new Set(leaseList.map(l => l.inpt_id)))
    }

    function selectWithData() {
        setSelected(new Set(leaseList.filter(l => l.has_data).map(l => l.inpt_id)))
    }

    function clearAll() {
        setSelected(new Set())
    }

    // ---- Filtered lease display ----
    const displayedLeases = leaseList.filter(l =>
        !filter || l.lease_name.toLowerCase().includes(filter.toLowerCase())
    )

    // ---- Download handler ----
    function handleDownload() {
        if (!data?.rows?.length) return

        // Apply lease filter to rows
        const filteredRows = selectedLeases.size > 0
            ? data.rows.filter(r => selectedLeases.has(r.inpt_id))
            : data.rows

        if (!filteredRows.length) {
            alert('No data for selected leases in this date range.')
            return
        }

        const dateStr = `${startDate}_${endDate}`
        let sheets = []

        if (format === 'daily') {
            sheets = [buildCCDailySheet(filteredRows)].filter(Boolean)
        } else if (format === 'monthly') {
            sheets = [buildCCMonthlySheet(filteredRows)].filter(Boolean)
        } else {
            sheets = buildCCQuarterlySheets(filteredRows).filter(Boolean)
        }

        if (sheets.length) {
            const label = format.charAt(0).toUpperCase() + format.slice(1)
            exportMultiSheet(sheets, `ComboCurve_${label}_${dateStr}`)
        }
    }

    // ---- Derived stats ----
    const totalRows = data?.rows?.length ?? 0
    const filteredRows = data?.rows?.filter(r => selectedLeases.has(r.inpt_id)) ?? []
    const selectedCount = selectedLeases.size

    // ---- Styles ----
    const bg      = isDark ? 'bg-slate-900' : 'bg-white'
    const border  = isDark ? 'border-slate-700' : 'border-gray-200'
    const text    = isDark ? 'text-white' : 'text-gray-900'
    const subtext = isDark ? 'text-gray-400' : 'text-gray-500'
    const inputCls = `px-3 py-1.5 rounded border text-sm ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`
    const cardCls  = `rounded-lg border p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`

    return (
        <div className={`space-y-5 ${text}`}>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold">ComboCurve Downloads</h1>
                    <p className={`text-sm mt-0.5 ${subtext}`}>
                        Generate lease-level production files for ComboCurve upload or investor updates
                    </p>
                </div>
                <button
                    onClick={fetchData}
                    disabled={loadingData}
                    className={`text-sm px-3 py-1.5 rounded border transition ${isDark ? 'border-slate-600 text-gray-300 hover:text-white hover:border-slate-400' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}
                >
                    {loadingData ? 'Loading…' : '↻ Refresh'}
                </button>
            </div>

            {error && (
                <div className="rounded border border-red-500 bg-red-900/20 text-red-400 px-4 py-3 text-sm">
                    {error}
                    <button onClick={fetchData} className="ml-3 underline">Retry</button>
                </div>
            )}

            {/* Format + Date Range */}
            <div className={`${cardCls} space-y-4`}>
                {/* Format toggle */}
                <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${subtext}`}>Format</label>
                    <div className="flex gap-2 flex-wrap">
                        {FORMATS.map(f => (
                            <button
                                key={f.id}
                                onClick={() => handleFormatChange(f.id)}
                                title={f.description}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition border ${
                                    format === f.id
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDark
                                            ? 'bg-slate-700 text-gray-300 border-slate-600 hover:bg-slate-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <p className={`text-xs mt-1.5 ${subtext}`}>
                        {FORMATS.find(f => f.id === format)?.description}
                    </p>
                </div>

                {/* Date range */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div>
                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${subtext}`}>Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <span className={`mt-5 ${subtext}`}>to</span>
                    <div>
                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${subtext}`}>End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                </div>
            </div>

            {/* Lease Selector */}
            <div className={`${cardCls} space-y-3`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <label className={`text-xs font-semibold uppercase tracking-wide ${subtext}`}>
                        Leases ({selectedCount} of {leaseList.length} selected)
                    </label>
                    <div className="flex gap-2">
                        <button onClick={selectWithData} className={`text-xs px-2 py-1 rounded border ${isDark ? 'border-slate-600 text-gray-300 hover:text-white' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}>
                            With Data
                        </button>
                        <button onClick={selectAll} className={`text-xs px-2 py-1 rounded border ${isDark ? 'border-slate-600 text-gray-300 hover:text-white' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}>
                            All
                        </button>
                        <button onClick={clearAll} className={`text-xs px-2 py-1 rounded border ${isDark ? 'border-slate-600 text-gray-300 hover:text-white' : 'border-gray-300 text-gray-600 hover:text-gray-900'}`}>
                            Clear
                        </button>
                    </div>
                </div>

                {/* Search */}
                <input
                    type="text"
                    placeholder="Filter leases…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className={`w-full ${inputCls}`}
                />

                {/* Lease grid */}
                {loadingLeases ? (
                    <div className={`text-sm ${subtext}`}>Loading leases…</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 max-h-56 overflow-y-auto pr-1">
                        {displayedLeases.map(l => {
                            const checked = selectedLeases.has(l.inpt_id)
                            return (
                                <label
                                    key={l.inpt_id}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition ${
                                        checked
                                            ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-800'
                                            : isDark ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                                    } ${!l.has_data ? 'opacity-50' : ''}`}
                                    title={l.has_data ? `ID: ${l.inpt_id}` : `${l.inpt_id} — no production data in DB`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleLease(l.inpt_id)}
                                        className="shrink-0 accent-blue-500"
                                    />
                                    <span className="truncate">{l.lease_name} <span className={`${subtext}`}>({l.chosen_id})</span></span>
                                </label>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Download bar */}
            <div className={`flex items-center justify-between flex-wrap gap-3 rounded-lg border px-4 py-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-sm ${subtext}`}>
                    {loadingData ? (
                        <span>Fetching data…</span>
                    ) : data ? (
                        <span>
                            <strong className={text}>{filteredRows.length.toLocaleString()}</strong> rows
                            {' '}({selectedCount} leases, {format})
                        </span>
                    ) : (
                        <span>Select dates and click refresh</span>
                    )}
                </div>

                <button
                    onClick={handleDownload}
                    disabled={loadingData || !filteredRows.length}
                    className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition ${
                        !loadingData && filteredRows.length
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : isDark ? 'bg-slate-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    ↓ Download Excel
                </button>
            </div>

            {/* Data preview table (first 10 rows) */}
            {!loadingData && filteredRows.length > 0 && (
                <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide border-b ${isDark ? 'bg-slate-800 border-slate-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                        Preview — first 10 rows
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                                    {['Lease', 'ID', '#', 'Date', 'Oil (BBL)', 'Gas (MCF)', 'Water (BBL)'].map(h => (
                                        <th key={h} className={`px-3 py-2 text-left font-semibold ${subtext}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.slice(0, 10).map((r, i) => (
                                    <tr key={i} className={`border-t ${isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-gray-100 hover:bg-gray-50'}`}>
                                        <td className="px-3 py-1.5 truncate max-w-[160px]">{r.lease_name}</td>
                                        <td className={`px-3 py-1.5 font-mono text-xs ${subtext}`}>{r.inpt_id}</td>
                                        <td className={`px-3 py-1.5 ${subtext}`}>{r.chosen_id}</td>
                                        <td className="px-3 py-1.5">{r.prod_date}</td>
                                        <td className="px-3 py-1.5 text-right">{r.oil_bbl?.toFixed(1)}</td>
                                        <td className="px-3 py-1.5 text-right">{r.gas_mcf?.toFixed(1)}</td>
                                        <td className="px-3 py-1.5 text-right">{r.water_bbl?.toFixed(1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filteredRows.length > 10 && (
                        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-slate-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
                            …and {(filteredRows.length - 10).toLocaleString()} more rows
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
